<script lang="ts" setup>
import { RealtimeReportService, SelectOptionsService, toastServiceError } from '@/services';
import store from '@/store';
import {
    AgentExtensionReportActions,
    AgentExtensionReportFilter,
    AgentExtensionReportGetters,
    MultiSelectOption,
    SelectOptionsServiceGetQueuesResult,
    SelectOptionsServiceGetUserLabelsResult,
    SelectOptionsServiceGetUsersResult,
    UserDeviceStatus,
} from '@/types';
import { FilterIcon } from '@heroicons/vue/solid';
import { computed, onMounted, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const defaultFilter = computed(() => store.getters[AgentExtensionReportGetters.DEFAULT_FILTER]);
const storeFilter = computed(() => store.getters[AgentExtensionReportGetters.FILTER]);

const filterOpened = ref(false);

const filter: Ref<AgentExtensionReportFilter> = ref({
    status: defaultFilter.value.status,
    user: defaultFilter.value.user,
    userLabel: defaultFilter.value.userLabel,
    queue: defaultFilter.value.queue,
});

// Options
const userOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);
const userLabelOptions: Ref<SelectOptionsServiceGetUserLabelsResult[]> = ref([]);
const queueOptions: Ref<SelectOptionsServiceGetQueuesResult[]> = ref([]);
const statusOptions: Ref<MultiSelectOption[]> = ref([
    {
        value: UserDeviceStatus.CONNECTED,
        label: t('report.realtimeAgentExtension.device.status.CONNECTED'),
    },
    {
        value: UserDeviceStatus.OFFLINE,
        label: t('report.realtimeAgentExtension.device.status.OFFLINE'),
    },
    {
        value: UserDeviceStatus.IN_PAUSE,
        label: t('report.realtimeAgentExtension.device.status.IN_PAUSE'),
    },
]);

watch(filterOpened, () => {
    refreshLocalFilter();
});

const fetchUsers = async () => {
    try {
        const response = await RealtimeReportService.getUserDevices();
        const users = new Map<number, SelectOptionsServiceGetUsersResult>();
        for (const userDevice of response.data) {
            const user = userDevice.user;
            const extensionNumber = user.extension?.number;

            if (extensionNumber === undefined) continue;

            if (!users.has(extensionNumber)) {
                users.set(extensionNumber, {
                    id: extensionNumber,
                    name: user.name,
                    labels: [],
                });
            }
        }
        userOptions.value = Array.from(users.values());
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchUserLabels = async () => {
    try {
        const response = await SelectOptionsService.getUserLabels();
        userLabelOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchQueues = async () => {
    try {
        const response = await SelectOptionsService.getQueues();
        queueOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const applyFilters = () => {
    store.dispatch(AgentExtensionReportActions.SET_FILTER, filter.value);
};

const clearFilters = () => {
    store.dispatch(AgentExtensionReportActions.CLEAR_FILTER);
    refreshLocalFilter();
};

const refreshLocalFilter = () => {
    filter.value.status = storeFilter.value.status;
    filter.value.user = storeFilter.value.user;
    filter.value.userLabel = storeFilter.value.userLabel;
    filter.value.queue = storeFilter.value.queue;
};

onMounted(() => {
    fetchUsers();
    fetchUserLabels();
    fetchQueues();
});
</script>

<template>
    <UiRightPanel v-model="filterOpened" :title="$t('filter.sidebar.title')">
        <template #button>
            <UiButton
                size="md"
                variant="white"
                text-variant="gray-700"
                class="focus:ring-primary border border-gray-300 shadow-none hover:bg-gray-50"
                @click="filterOpened = true"
            >
                <FilterIcon class="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                {{ $t('core.actions.Filter') }}
            </UiButton>
        </template>
        <template #default>
            <form autocomplete="off" class="flex flex-col gap-3">
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
                    <div class="sm:col-span-5">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('report.realtimeAgentExtension.filters.status') }}
                        </label>
                        <UiMultiselect
                            name="filter_type"
                            v-model="filter.status"
                            :options="statusOptions"
                            can-clear
                            :placeholder="t('core.options.All')"
                        />
                    </div>
                </div>
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
                    <div class="sm:col-span-5">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('report.realtimeAgentExtension.filters.user') }}
                        </label>
                        <UiMultiselect
                            name="filter_user"
                            v-model="filter.user"
                            :options="userOptions"
                            label="name"
                            value-prop="id"
                            can-clear
                            :placeholder="t('core.options.All')"
                        />
                    </div>
                </div>
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
                    <div class="sm:col-span-5">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('report.realtimeAgentExtension.filters.userLabel') }}
                        </label>
                        <UiMultiselect
                            name="filter_userLabel"
                            v-model="filter.userLabel"
                            label="label"
                            value-prop="id"
                            :options="userLabelOptions"
                            :placeholder="t('core.options.All')"
                            can-clear
                        />
                    </div>
                </div>
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
                    <div class="sm:col-span-5">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('report.realtimeAgentExtension.filters.queue') }}
                        </label>
                        <UiMultiselect
                            name="filter_queue"
                            v-model="filter.queue"
                            :options="queueOptions"
                            label="name"
                            value-prop="id"
                            can-clear
                            :placeholder="t('core.options.All')"
                        />
                    </div>
                </div>
                <div class="mt-4 grid grid-cols-2 place-content-around gap-4">
                    <UiButton variant="white" text-variant="gray-700" @click="clearFilters()">
                        {{ $t('core.actions.Clear') }}
                    </UiButton>
                    <UiButton variant="primary" text-variant="white" @click="applyFilters()">
                        {{ $t('core.actions.Apply') }}
                    </UiButton>
                </div>
            </form>
        </template>
    </UiRightPanel>
</template>
